import { create } from 'zustand';

export type UnsavedFormCallbacks = {
  hasUnsavedChanges: () => boolean;
  onCancel?: () => void;
  onConfirm?: () => Promise<void>;
  onTrigger?: () => Promise<boolean>;
};

type UnsavedChangesStore = {
  forms: Record<string, UnsavedFormCallbacks>;
  registerForm: (formId: string, callbacks: UnsavedFormCallbacks) => void;
  unregisterForm: (formId: string) => void;
  getDirtyForm: (formId?: string) => { formId: string; callbacks: UnsavedFormCallbacks } | null;
  hasUnsavedChanges: () => boolean;
};

const isDirtyForm = (callbacks: UnsavedFormCallbacks): boolean => {
  try {
    return callbacks.hasUnsavedChanges();
  } catch {
    return true;
  }
};

const findDirtyForm = (
  forms: Record<string, UnsavedFormCallbacks>,
  formId?: string,
): { formId: string; callbacks: UnsavedFormCallbacks } | null => {
  if (formId) {
    const callbacks = forms[formId];
    if (!callbacks || !isDirtyForm(callbacks)) return null;

    return { formId, callbacks };
  }

  for (const [id, callbacks] of Object.entries(forms)) {
    if (isDirtyForm(callbacks)) return { formId: id, callbacks };
  }

  return null;
};

export const useUnsavedChangesStore = create<UnsavedChangesStore>((set, get) => ({
  forms: {},
  registerForm: (formId, callbacks) => {
    set((state) => ({
      forms: { ...state.forms, [formId]: callbacks },
    }));
  },
  unregisterForm: (formId) => {
    set((state) => {
      const forms = { ...state.forms };
      delete forms[formId];
      return { forms };
    });
  },
  getDirtyForm: (formId) => {
    return findDirtyForm(get().forms, formId);
  },
  hasUnsavedChanges: () => get().getDirtyForm() !== null,
}));
